/**
 * Markdown
 *
 * Chat message renderer built on `react-markdown` (+ GFM, line breaks, gemoji,
 * smart typography) with safe inline HTML via rehype-raw + rehype-sanitize.
 * Replaces the hand-rolled `messageFormatting` parser; the `components` map
 * keeps the previous Tailwind look, the code-copy button, table scroll wrapper,
 * links, and per-message RTL.
 *
 * @module @ideoz/ui/components/molecules/Markdown
 */

import React, { useCallback, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkGemoji from 'remark-gemoji';
import remarkSmartypants from 'remark-smartypants';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { Copy, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface MarkdownProps {
  children: string;
  /** Renders right-to-left (Arabic). Decided per message by the caller. */
  isRtl?: boolean;
  className?: string;
}

/** App marker the model emits to flag a follow-up prompt. */
const FOLLOW_UP_RE = /<\s*FOLLOW_UP\s*\/?>/gi;

/** Flatten a React node tree to its text (for the code-copy button). */
const toText = (node: React.ReactNode): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toText).join('');
  if (React.isValidElement(node))
    return toText((node.props as { children?: React.ReactNode }).children);
  return '';
};

const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(() => {
    try {
      void navigator.clipboard.writeText(toText(children).replace(/\n$/, ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  }, [children]);

  return (
    <div className="relative mb-3 group max-w-full">
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy code"
        className="absolute right-2 top-2 z-10 rounded-md bg-white/80 p-1 text-gray-500 opacity-0 transition-opacity hover:text-gray-800 group-hover:opacity-100"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
      {/* Its own text colour: inside a dark user bubble the inherited white was unreadable. */}
      <pre className="max-w-full overflow-x-auto rounded-lg bg-gray-50 p-4 font-mono text-gray-900">
        {children}
      </pre>
    </div>
  );
};

/**
 * Sanitize schema based on the default, extended to keep the fenced-code
 * `language-*` class on <code> (any className value allowed on code).
 */
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [['className']],
  },
};

const components: Components = {
  h1: ({ children, ...p }) => (
    <h1 className="mb-3 text-xl font-semibold" {...p}>
      {children}
    </h1>
  ),
  h2: ({ children, ...p }) => (
    <h2 className="mb-2 text-lg font-semibold" {...p}>
      {children}
    </h2>
  ),
  h3: ({ children, ...p }) => (
    <h3 className="mb-2 text-base font-semibold" {...p}>
      {children}
    </h3>
  ),
  h4: ({ children, ...p }) => (
    <h4 className="mb-1 font-semibold" {...p}>
      {children}
    </h4>
  ),
  h5: ({ children, ...p }) => (
    <h5 className="mb-1 font-semibold" {...p}>
      {children}
    </h5>
  ),
  h6: ({ children, ...p }) => (
    <h6 className="mb-1 font-semibold" {...p}>
      {children}
    </h6>
  ),
  p: ({ children, ...p }) => (
    <p className="mb-3 leading-relaxed last:mb-0" {...p}>
      {children}
    </p>
  ),
  a: ({ children, href, ...p }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline hover:text-blue-800"
      {...p}
    >
      {children}
    </a>
  ),
  ul: ({ children, ...p }) => (
    <ul className="mb-3 ml-6 list-disc space-y-1" {...p}>
      {children}
    </ul>
  ),
  ol: ({ children, ...p }) => (
    <ol className="mb-3 ml-6 list-decimal space-y-1" {...p}>
      {children}
    </ol>
  ),
  li: ({ children, className, ...p }) => (
    <li className={cn('leading-relaxed', className)} {...p}>
      {children}
    </li>
  ),
  blockquote: ({ children, ...p }) => (
    <blockquote className="my-4 border-l-4 border-gray-300 pl-4 italic text-gray-700" {...p}>
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-t border-gray-300" />,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="text-gray-500 line-through">{children}</del>,
  pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
  code: ({ className, children, ...p }) => {
    const isBlock = /language-/.test(className || '') || toText(children).includes('\n');
    if (isBlock) {
      return (
        <code className={cn('font-mono', className)} {...p}>
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-gray-900 break-words">
        {children}
      </code>
    );
  },
  table: ({ children }) => (
    <div className="relative mb-3 max-w-full overflow-x-auto scrollbar-chat">
      <table className="min-w-full max-w-full border-collapse border border-gray-300">
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...p }) => (
    <th className="border border-gray-300 bg-gray-50 px-3 py-2 text-start font-semibold" {...p}>
      {children}
    </th>
  ),
  td: ({ children, ...p }) => (
    <td className="border border-gray-300 px-3 py-2 align-top" {...p}>
      {children}
    </td>
  ),
};

/**
 * Markdown Component
 */
export const Markdown: React.FC<MarkdownProps> = ({ children, isRtl = false, className = '' }) => {
  const hasFollowUp = FOLLOW_UP_RE.test(children);
  // Strip the app marker so it doesn't leak as literal text; render its badge.
  const content = hasFollowUp ? children.replace(FOLLOW_UP_RE, '') : children;

  return (
    <div
      dir={isRtl ? 'rtl' : 'ltr'}
      className={cn('', isRtl ? 'text-right' : 'text-left', className)}
    >
      {hasFollowUp && (
        <span className="mb-2 mr-2 inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
          💡 Follow-up
        </span>
      )}
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks, remarkGemoji, remarkSmartypants]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default Markdown;
